import { flatten } from 'flat';

import {
    Locale,
    StringMap,
    ProgramTheme,
    DefaultMessages,
    Messages,
    ProgramThemeMessages,
    SegmentSpecificMessages,
    Segment,
    ViewModel,
    Country,
    StudentPersonalInfoViewModel,
    MessagesModule,
    RequestOrganizationMessages,
} from '../types/types';
import { assertValidLocale, isValidLocale } from '../types/assertions';
import { getOptions } from '../../options/options';
import { logger } from '../utils/logger/logger';
import { DEFAULT_LOCALE } from '../../constants';

import { messages as enUS, requestOrganizationMessages as requestOrg } from './messages/en-US';

/**
 * @private
 * NOTE: webpack will not import chunks recursively, or with more than one variable in the path.
 * Here we're using a fully statically-defined import, for reliability.
 */
export const getMessagesModuleByLocale = async (locale: Locale): Promise<MessagesModule> => {
    let importPromise: Promise<any>;

    switch (locale) {
        case 'en-US':
            // Always have en-US available. Code-splitting is currently a problem for library consumers (HD-525)
            importPromise = Promise.resolve({ messages: enUS });
            break;
        // Example for how we want to do things once the library consumer code-split issue is resolved:
        // case 'fr':
            // TODO restore code-splitting once library users are able to serve it over the network from node_modules
            // importPromise = import(/* webpackChunkName: "messages_fr" */ './messages/fr');
            // break;
        default:
            logger.warn(`getMessagesModuleByLocale: Unable to load messages for ${locale}, falling back to en-US`);
            importPromise = Promise.resolve({ messages: enUS });
    }
    return importPromise.then(messagesModule => messagesModule.messages)
        .catch(() => logger.error(`Unable to load messages for ${locale}`));
};

/**
 * @description Pluck only the messages we want to override defaults with.
 *      Older versions of the theme response included all messages, but we don't want to overwrite all defaults.
 * @private
 */
export const getThemeMessages = (msgs: ProgramThemeMessages): ProgramThemeMessages => {
    return {
        companyName: msgs.companyName,
        step: {
            personalInfo: {
                title: msgs.step.personalInfo.title,
            },
            success: {
                redirectUrl: msgs.step.success.redirectUrl,
                redirectButtonText: msgs.step.success.redirectButtonText,
                subtitle: msgs.step.success.subtitle,
                title: msgs.step.success.title,
            },
            docUpload: {
                title: msgs.step.docUpload.title,
                subtitle: msgs.step.docUpload.subtitle,
            },
        },
    };
};

const getOptionsMessages = (locale: Locale): Object => {
    let optionalMessages: Object = {};
    if (getOptions().messagesWithLocale[locale]) {
        optionalMessages = getOptions().messagesWithLocale[locale];
    } else if (getOptions().messagesWithLocale[locale]) {
        optionalMessages = getOptions().messagesWithLocale[locale];
    }
    return optionalMessages;
};

/**
 * @todo tests
 * @private
 */
export const countryToLocale = (country: Country): Locale => {
    let locale: Locale;
    switch (country) {
        case 'US':
            locale = 'en-US';
        default:
            locale = 'en-US';
    }
    return locale;
};


/**
 * @todo tests
 * @description Get all messages for a given locale.
 *      Return a flattened messages object, where properties are dot-string style.
 * @private
 */
export const getMessages = async (locale: Locale, programThemeMessages: ProgramThemeMessages, segment: Segment): Promise<Messages> => {
    if (!(isValidLocale(locale))) {
        logger.error(`Invalid locale ${locale}, falling back to en-US`);
        locale = 'en-US';
    }

    let defaultMessages: DefaultMessages | object = {};
    let segmentSpecificMessages: SegmentSpecificMessages | object = {};
    try {
        const messagesModule: MessagesModule = await getMessagesModuleByLocale(locale);
        defaultMessages = messagesModule.defaultMessages;
        segmentSpecificMessages = messagesModule.segmentMessages[segment];
    } catch (e) {
        logger.error(e);
    }

    const optionsMessages = getOptionsMessages(locale);
    const cleanThemeMessages = getThemeMessages(programThemeMessages);

    // Flatten these before object.assign so they are dot-props, so nested objects aren't overwritten, deleting properties
    const messages: Messages = Object.assign(
        {} as Messages,
        defaultMessages ? flatten(defaultMessages) : {},
        cleanThemeMessages ? flatten(cleanThemeMessages) : {},
        segmentSpecificMessages ? flatten(segmentSpecificMessages) : {},
        optionsMessages ? flatten(optionsMessages) : {},
    );
    return messages;
};

/**
 * @todo tests
 * @description Get all messages for a given locale.
 *      Return a flattened messages object, where properties are dot-string style.
 * @private
 */
export const getRequestOrgMessages = async (locale: Locale, RequestOrganizationMessages): Promise<RequestOrganizationMessages> => {
    if (!(isValidLocale(locale))) {
        logger.error(`Invalid locale ${locale}, falling back to en-US`);
        locale = 'en-US';
    }

    // Flatten these before object.assign so they are dot-props, so nested objects aren't overwritten, deleting properties
    const messages: RequestOrganizationMessages = Object.assign(
        {} as RequestOrganizationMessages,
        requestOrg ? flatten(requestOrg) : {},
    );
    return messages;
};

/**
 * @description
 */
export const getLocaleSafely = (viewModel: ViewModel): Locale => {
    let returnedLocale: Locale = 'en-US'; // default
    let newLocale: Locale;

    let chosenLocale: Locale;
    let country: Country;

    try {
        // Only in specific circumstances will we receive a locale choice
        chosenLocale = (viewModel as StudentPersonalInfoViewModel).localeChoice.value;
    } catch (e) {
        // Don't throw for property access errors here... they may not have existed (by design)
    }

    try {
        // Only in specific circumstances will we receive a country choice
        country = (viewModel as StudentPersonalInfoViewModel).countryChoice.value;
    } catch (e) {
        // Don't throw for property access errors here... they may not have existed (by design)
    }

    if (chosenLocale) {
        newLocale = chosenLocale;
    } else if (country) {
        newLocale = countryToLocale(country);
    }

    if (newLocale) {
        // If we _did_ get it, make sure it's valid
        returnedLocale = newLocale;
    }

    return returnedLocale;
};

/**
 * @deprecated Use `getMessages()` instead
 */
export const getIntlMessages = (locale: Locale, programTheme: ProgramTheme = null): StringMap => {
    assertValidLocale(locale);

    let optionalMessages: Object = {};
    if (getOptions().messagesWithLocale[locale]) {
        optionalMessages = getOptions().messagesWithLocale[locale];
    } else if (getOptions().messagesWithLocale[DEFAULT_LOCALE]) {
        optionalMessages = getOptions().messagesWithLocale[DEFAULT_LOCALE];
    }

    let sheerIdMessages: Object = {};
    if (programTheme) {
        try {
            sheerIdMessages = programTheme.intl.messages;
        } catch (e) {
            logger.error('Error accessing programTheme.intl.messages');
        }
    }
    // react-intl doesn't allow the messages object to contain nested objects.
    // Use flatten() so we can access nested objects with dot-strings like 'foo.bar'
    return flatten(Object.assign({}, sheerIdMessages, optionalMessages)) as StringMap;
};

export const getLocale = (programTheme: ProgramTheme = null): Locale => {
    let locale: Locale = getOptions().locale;

    if (programTheme) {
        try {
            locale = programTheme.intl.locale as Locale;
        } catch (e) {
            logger.error('Error accessing programTheme.intl.locale. Did the shape of an object/type change?');
        }
        if (!isValidLocale(locale)) {
            logger.warn(`Invalid locale "${locale}" received from server. Using fallback "${getOptions().locale}" instead.`);
            locale = getOptions().locale;
        }
    }
    return locale;
};

/**
 * @deprecated
 */
export const setLocale = () => {
    logger.error('setLocale() is deprecated, use verificationService.updateLocale() instead.');
};
